import React from 'react';
import { View } from 'react-native';

import Item from './components/item';
import Loading from '@components/loading';
import Refresh from '@components/common/Refresh';
import DataGrid from '@components/common/DataGrid';

import { isPhone, calculateTableConfig, deepClone } from '@helpers';
import { scale as initialScale, fullWidth } from '@components/frame';
import { TILE_CONFIG } from '@v2/pages/common/constants/base.constants';

import Image from '@v2/context/Image/context';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { SCREEN_PADDING_HORIZONTAL, TABLE_NAMES, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useHandleImageNavigation } from '@v2/pages/common/hooks/useHandleImageNavigation';
import { font as initialFont } from '@styles/fonts';
import { handleImagePress, getDataGridColumns } from './helpers';

export default (props) => {
  const {
    searchResult,
    isError,
    loaded,
    isSmallMode,
    isLineMode,
    fetchData,
    generateImageURL,
    recentlyViewed,
    setRecentlyViewed,
    isTableMode,
    facets,
    attachMode,
    withoutHorizontalPaddings,
    containerWidth = fullWidth,
    withoutVerticalCentered,
    withoutLoadingIndicator,
    scale = initialScale,
    font = initialFont,
    onImagePress,
    isPopupMode,
    popupWidth,
    isSearchScreen,
    onItemSelect,
  } = props;

  const { IS_NARROW_WEB } = useDeviceSize();
  const { handleImageNavigation } = useHandleImageNavigation();

  const effectiveWidth = isPopupMode && popupWidth ? popupWidth : containerWidth;

  const config = {
    width: effectiveWidth,
    scale,
    initialBaseSize: isSmallMode ? TILE_CONFIG.BASE_WIDTH_SMALL : TILE_CONFIG.BASE_WIDTH,
    spaceBetween: isSmallMode ? TILE_CONFIG.SPACE_BETWEEN_SMALL : TILE_CONFIG.SPACE_BETWEEN_NORMAL,
    withoutHorizontalPaddings: withoutHorizontalPaddings && isPhone,
  };

  const { columns, itemSize } = calculateTableConfig(config);

  const data = deepClone(searchResult);

  const additions = columns - (data.length % columns);
  if (additions !== columns) {
    for (let i = 0; i < additions; i++) {
      data.push('empty');
    }
  }

  const onPress = ({ image, showActions }) => {
    handleImagePress({
      image,
      showActions,
      onItemSelect,
      onImagePress,
      handleImageNavigation,
    });
  };

  const dataGridColumns = getDataGridColumns(scale, facets);
  dataGridColumns[0].renderCell = ({ row: item }) => {
    const imgSize = { width: scale(50), height: scale(30) };
    const imageData = {
      image: {
        documentId: item.documentId,
        imageHash: item.contentHash,
        imageData: {
          isCropped: item.isCropped,
          extensionDerivative: item.extensionDerivative,
          pageNumber: item.pageNumber,
          rotationAngle: item.imageDetails?.find((i) => i.pageNumber === item.pageNumber)?.rotationAngle || 0,
          rotationAngleCrop: item.imageCropSettings?.find((i) => i.pageNumber === item.pageNumber)?.rotationAngle || 0,
        },
        width: imgSize.width,
        height: imgSize.height,
        onPress: () => onPress({ image: item, showActions: false }),
      },
    };
    return (
      <View style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
        <Image {...imageData} attachMode={attachMode} scale={scale} />
      </View>
    );
  };

  if (isTableMode) {
    return (
      <View
        testID={isSearchScreen ? TEST_IDS.searchScreen.imagesDocuments.dataGridContainer : undefined}
        style={[{ width: effectiveWidth, height: '100%' }]}
      >
        <DataGrid
          {...{
            ...props,
            primaryKey: 'fileName',
            columns: dataGridColumns,
            data,
            idName: 'documentId',
            tableName: props.tableName || TABLE_NAMES.images,
            onRowClick: (item) => onPress({ image: item, showActions: true }),
          }}
        />
      </View>
    );
  }

  if (isError) {
    return <Refresh onPress={fetchData} />;
  }

  if (!loaded && !withoutLoadingIndicator) {
    return <Loading />;
  }

  return (
    <View style={[{ width: effectiveWidth, flex: 1 }]}>
      <View
        testID={isSearchScreen ? TEST_IDS.searchScreen.imagesDocuments.itemContainer : undefined}
        style={[
          {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            marginTop: scale(IS_NARROW_WEB ? 11 : isPhone ? -4 : -5),
            justifyContent: 'space-between',
            flexDirection: 'row',
            flexWrap: 'wrap',
          },
          !withoutVerticalCentered && { alignItems: 'center' },
          !withoutHorizontalPaddings && { paddingLeft: SCREEN_PADDING_HORIZONTAL, paddingRight: SCREEN_PADDING_HORIZONTAL },
          isLineMode && { marginTop: 0 },
        ]}
      >
        {data.map((item, index) => (
          <Item
            key={'Item' + index}
            {...{
              index,
              itemSize,
              item,
              isEmpty: item === 'empty',
              isSmallMode,
              isLineMode,
              generateImageURL,
              recentlyViewed,
              setRecentlyViewed,
              attachMode,
              containerWidth: effectiveWidth,
              withoutHorizontalPaddings,
              onPress,
              scale,
              font,
            }}
          />
        ))}
      </View>
    </View>
  );
};
